// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import BackdropProgress from '../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CustomSnackbar from '../ui/Snackbars';
import EditIcon from '@mui/icons-material/Edit';
import EditVisaDialog from './EditVisaDialog';
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress';
import no_image_available from '../../images/no_image_available.jpeg';
import Paper from '@mui/material/Paper';
import React from 'react';
import useHttp from '../../utils/http';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB, imageContentToImage } from '../../utils/formatting';
import { getEmployeeVisaById, getEmployeeVisaImage, getEmployeeTravelDocumentById } from '../../utils/end-points.js';
import { getVisaStatusColor } from '../../theme/status-colors';
import { isAccessGranted } from "../../utils/oauth";
import { PRIVILEGES } from "../../Config.js";
import { useState, useEffect, useCallback } from 'react';
import "../../css/VisaInfoCard.scss"
import { visaStatus } from '../../utils/const';

const VisaInfoCard = (props) => {

    const [selectedVisa, setSelectedVisa] = useState()
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [isLoading, setIsLoading] = useState(true);
    const [editVisaDialog, setEditVisaDialog] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [imageName, setImageName] = useState("");
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [passport, setPassport] = useState(null)
    const [isPassportsLoading, setPassportsLoading] = useState(false)
    const { handleRequest, handleFileRequest } = useHttp()

    useEffect(() => {
        // select visa popup
        if (!props.selectedVisa && props.id) {
            fetchVisaInfo()
        }
        // visa list card view
        else {
            setSelectedVisa(props.selectedVisa)
            setIsLoading(false)
            fetchVisaImage(props.selectedVisa.id, props.selectedVisa.email)
            fetchUserPassport(props.selectedVisa?.passportId, props.selectedVisa.email)
        }

    }, [props.selectedVisa, props.id])

    const fetchUserPassport = (passportId, email) => {
        if(passportId) {
            setPassportsLoading(true)

            handleRequest(getEmployeeTravelDocumentById.replace("$id", passportId).replace("$email", email), "GET", null, (travelDocument) => {
                if (travelDocument) {
                    setPassport(travelDocument)
                }
                else if (!travelDocument) {
                    setSnackbarData({ message: "No Passport for this Visa", open: true, severity: "error", onClose: onCloseSnackbar })
                }
                setPassportsLoading(false)
            }, (error) => {
                setPassportsLoading(false)
                console.error(error)
                setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
            })
        }
        else {
            setPassport(null)
        }
    }

    const fetchVisaImage = useCallback(async (visaId, email) => {
        setIsImageLoading(true)

        let url = getEmployeeVisaImage.replace("$email", email).replace("$id", visaId)
        handleFileRequest(url, "GET", null, (data) => {
            if (data.blobContent) {
                setImageSrc(imageContentToImage(data.blobContent))
                setImageName("Travel Document Image")
                setIsImageLoading(false)
            }
            else {
                setImageName("No image available")
                setImageSrc(no_image_available)
                setIsImageLoading(false)
            }
        }, (error) => {
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }, [props.selectedVisa])

    const fetchVisaInfo = useCallback(async () => {
        setIsLoading(true)
        let url = getEmployeeVisaById.replace("$email", props.email).replace("$id", props.id)
        handleRequest(url, "GET", null, (visa) => {
            console.log(visa)
            if (visa) {
                setSelectedVisa(visa)
                fetchVisaImage(visa.id, props.email)
                fetchUserPassport(visa?.passportId, props.email)
            }
            else {
                setSnackbarData({ message: "Error while fetching Visa information", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setIsLoading(false)
        }, (error) => {
            setIsLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }, [props.id])

    const getVisaStatus = () => {
        var visaEndDate = adjustForTimezone(new Date(selectedVisa.endDate))
        var currentDate = new Date()

        return <span className='Status-chip'
            style={{
                backgroundColor: getVisaStatusColor(getDateForDB(currentDate) <= getDateForDB(visaEndDate)),
                color: "white"
            }}>
            {(getDateForDB(currentDate) <= getDateForDB(visaEndDate)) ? visaStatus.ACTIVE : visaStatus.EXPIRED}
        </span>
    }

    const editVisa = () => {
        setEditVisaDialog(true)
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    const handleCloseEditVisaDialog = () => {
        setEditVisaDialog(false)
    }

    return (
        <React.Fragment>

            <CustomSnackbar snackbar={snackbarData} />

            <BackdropProgress open={isLoading} />

            {selectedVisa && editVisaDialog && <EditVisaDialog open={editVisaDialog} onClose={handleCloseEditVisaDialog} visa={selectedVisa} image={imageSrc === no_image_available ? null : { src: imageSrc, name: imageName }} refreshVisaList={() => props.refreshVisaList()} />}

            <div className='Visa-info-cards'>
                {!isLoading && (
                    <Paper className='Details-paper Border-elevation'>
                        <div className='Visa-info-title'>
                            <span>Visa Details</span>
                            <span className='Edit-visa-span'>{isAccessGranted([PRIVILEGES.EDIT_VISA]) && props.selectedVisa && <Button variant={"contained"} startIcon={<EditIcon />} color="secondary" className='Edit-visa-button'
                                onClick={() => editVisa()}>Edit Visa</Button>}</span>
                        </div>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Email : </Grid>
                            <Grid item lg={8}>{selectedVisa.email}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Country : </Grid>
                            <Grid item lg={8}>{getCountryNameFromCountryCode(selectedVisa.country) ? getCountryNameFromCountryCode(selectedVisa.country) : selectedVisa.country}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Visa Type : </Grid>
                            <Grid item lg={8}>{selectedVisa.visaType}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Entry Type : </Grid>
                            <Grid item lg={8}>{selectedVisa.entryType}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Start Date : </Grid>
                            <Grid item lg={8}>{getDateForDB(adjustForTimezone(new Date(selectedVisa.startDate)))}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>End Date : </Grid>
                            <Grid item lg={8}>{getDateForDB(adjustForTimezone(new Date(selectedVisa.endDate)))}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span'>Issued Date : </Grid>
                            <Grid item lg={8}>{getDateForDB(adjustForTimezone(new Date(selectedVisa.issuedDate)))}</Grid>
                        </Grid>
                        <Grid container className='Visa-info-entry'>
                            <Grid item lg={4} className='Visa-info-span Grid-item-center'>Passport : </Grid>
                            <Grid item lg={8} className='Grid-item-center'>
                                {isPassportsLoading
                                    ? <Box sx={{ width: '100%' }}>
                                        <LinearProgress color="secondary" />
                                    </Box> : (passport
                                        ? getCountryNameFromCountryCode(passport.issuedCountry) + "  [" + passport.documentNo + "]"
                                        : "No Passport Tagged")}
                                {/* {selectedVisa.passportId?<Button className='View-passport-button' size='small' variant='outlined' color='primary'>View Passport</Button>:<p>No Passport</p>} */}
                            </Grid>
                        </Grid>
                        {
                            !isPassportsLoading && passport && (
                                <Grid container className='Visa-info-entry'>
                                    <Grid item lg={4} className='Visa-info-span Grid-item-center'>Passport Validity Period: </Grid>
                                    <Grid item lg={8} className='Grid-item-center'>
                                        {getDateForDB(adjustForTimezone(new Date(passport.issuedDate))) + " - " + getDateForDB(adjustForTimezone(new Date(passport.expiryDate)))}
                                    </Grid>
                                </Grid>
                            )
                        }
                        {props.selectedVisa && (
                            <Grid container className='Visa-info-entry'>
                                <Grid item lg={4} className='Visa-info-span'>Visa Status : </Grid>
                                <Grid item lg={8}>{getVisaStatus()}</Grid>
                            </Grid>
                        )}
                        <div className={props.id ? 'Visa-image-div-for-popup' : 'Visa-image-div-for-card'}>
                            {isImageLoading ? <CircularProgress className='Secondary-loader' color="secondary" /> : <img id="image" src={imageSrc} alt={imageName} className='Visa-image' />}
                        </div>
                    </Paper>
                )}
            </div>

        </React.Fragment>
    );
}

export default VisaInfoCard;